import { faArchive, faFolders } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useShoppingCartsContext } from 'context/ShoppingCartsContext';
import { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Swal, { SweetAlertResult } from 'sweetalert2';

import * as apiRecipeImageSearch from 'api/aiFunctions/PostSimilarRecipesByImageV1';
import MenuPlan from 'classes/MenuPlan/Detail/MenuPlan';
import Recipe from 'classes/Recipe/Search/Recipe';
import RecipeList from 'classes/Recipe/Search/RecipeList';
import Search from 'classes/Recipe/Search/Search';
import RecipeAdvancedSearch from 'components/desktop/Recipe/AdvancedSearch/AdvancedSearch';
import BtnGroup, { BtnData } from 'components/desktop/_general/Button/BtnGroup/BtnGroup';
import BtnNew from 'components/desktop/_general/Button/BtnNew/BtnNew';
import { RenderIf } from 'components/desktop/_general/Conditional/RenderIf';
import DropdownPageSize from 'components/desktop/_general/DropdownPageSize/DropdownPageSize';
import DropdownSortOrder from 'components/desktop/_general/DropdownSortOrder/DropdownSortOrder';
import LoadingAnimation from 'components/desktop/_general/Loading/LoadingAnimation';
import NavigationPillsButtons from 'components/desktop/_general/NavigationPills/NavigationPillsButtons';
import NavigationPillsContent from 'components/desktop/_general/NavigationPills/NavigationPillsContent';
import Pagination from 'components/desktop/_general/Pagination/Pagination';
import AdvancedSearchInput from 'components/desktop/_general/Search/AdvancedSearchInput';
import {
	AirListRowDiscriminatorName,
	IAirListRow,
} from 'components/desktop/_general/SearchResultList/IAirListRow';
import SearchResultList from 'components/desktop/_general/SearchResultList/SearchResultList';
import SearchResultThumbnail, {
	ThumbnailCard,
} from 'components/desktop/_general/SearchResultThumbnail/SearchResultThumbnail';
import { numberOfRecommendations } from 'constants/imageSearchNumberOfRecommendations';
import { RecipeSortOrder } from 'enums/SortOrder/RecipeSortOrder';
import clone from 'functions/clone';
import delay from 'functions/delay';
import { getPicturePath } from 'functions/getPicturePath';
import { QueryParams, getQueryParams } from 'functions/getQueryParams';
import getSeasonalityCode from 'functions/getSeasonalityCode';
import usePreferredAllergens from 'hooks/redux/usePreferredAllergens';
import useEffectWhenMounted from 'hooks/useEffectWhenMounted';
import { usePermissions } from 'hooks/usePermissions';
import { ISelectItem } from 'interfaces/ISelectItem';
import { useMenuHandlers } from 'reducers/MenuReducer';
import { RootState } from 'reducers/rootReducer';
import { MenuEditType } from 'types/Menu/Menu';
import EntityType from 'types/ShoppingCart/EntityType';

interface IProps {
	archive?: boolean;
}

const maxNumberOfButtons = 10;

function getLocation(location: string) {
	if (location.includes('/search/menuCopy')) {
		return 'menuCopy';
	}
	if (location.includes('/search/menuPlan')) {
		return 'menuPlan';
	}
	if (location.includes('/search/menu')) {
		return 'menu';
	}
	if (location.includes('/search/ingredient')) {
		return 'ingredient';
	}
	return 'recipe';
}

const RecipeSearchComponent = (props: IProps) => {
	const { t } = useTranslation();
	const [isLoading, setIsLoading] = useState(false);
	const [recipeList, setRecipeList] = useState<RecipeList>(new RecipeList());
	const [search, setSearch] = useState<Search>(new Search());
	const [renderRanges, setRenderRanges] = useState<boolean>(false);
	const [searchTerm, setSearchTerm] = useState<string>(search.searchTerm.value ?? '');
	const preferredAllergens = usePreferredAllergens();
	const params = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const resourceType = getLocation(location.pathname);
	const reduxCurrency: string = useSelector((state: RootState) => state.currency);
	const reduxCultureCode: string = useSelector((state: RootState) => state.cultureCode);
	const reduxPreferredNutrientIds = useSelector((state: RootState) => state.preferredNutrientIds);
	const reduxValueScope = useSelector((state: RootState) => state.valueScope);
	const [searchIsLoading, setSearchIsLoading] = useState(false);

	const isNewMenu = location.pathname.includes('/recipe/search/menu/');
	const { isRecipeEditor } = usePermissions();
	const carts = useShoppingCartsContext();

	useEffect(() => {
		// @ts-ignore
		const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
		// @ts-ignore
		tooltipTriggerList.forEach(function (tooltipTriggerEl) {
		  // @ts-ignore
		  new window.bootstrap.Tooltip(tooltipTriggerEl);
		});
	  }, []);

	useEffect(() => {
		initialize();
	}, []);

	useEffectWhenMounted(() => {
		initialize();
	}, [reduxCultureCode, preferredAllergens]);

	const initialize = async (): Promise<void> => {
		if (window.location.href.includes('?')) {
			setSearchIsLoading(true);
			setIsLoading(true);
			await search.initialize();
			if (props.archive !== undefined && props.archive == true) {
				search.showOnlyDeleted = true;
			}
			openAdvancedSearch();
			setSearchIsLoading(false);
			const queryParams: QueryParams = getQueryParams();
			search.mapFromUrl(queryParams);
			await getRecipes();
			setIsLoading(false);
		} else {
			setSearchIsLoading(true);
			await getRecipes();
			await search.initialize();
			if (props.archive !== undefined && props.archive == true) {
				search.showOnlyDeleted = true;
			}
			setSearchIsLoading(false);
		}
		setSearch(clone(search));
	};

	const handleChangeUrl = () => {
		const url: string | null = search.mapToUrl();
		if (search.recipeIds !== null) {
			if (url) navigate(url);
			getRecipes();
		} else {
			setRecipeList(new RecipeList());
			setIsLoading(false);
		}
	};

	const hadleChangeAdvancedSearchUrl = () => {
		search.pageIndex.value = 0;
		handleChangeUrl();
	};

	const openAdvancedSearch = () => {
		const advancedSearchButton = document.getElementById('advancedSearchButton');
		if (advancedSearchButton) advancedSearchButton.click();
	};

	const getRecipes = async (): Promise<void> => {
		setIsLoading(true);
		const response = await recipeList.callApi(
			search.mapToRequest(
				reduxCurrency,
				reduxValueScope,
				reduxPreferredNutrientIds,
				preferredAllergens
			)
		);
		response.do((x) => {
			recipeList.mapFromApi(x);
			setRecipeList(clone(recipeList));
			search.mapFromApi(x);
		});
		setIsLoading(false);
	};

	const handleReset = () => {
		search.reset();
		setSearchTerm('');
		handleChangeUrl();
	};

	const handleSearch = (input?: string) => {
		if (search.recipeIds === null) {
			search.recipeIds = [];
		}
		search.setSearchTerm(input ?? searchTerm);
		handleChangeUrl();
	};

	const handleChangeSortOrder = (value: string) => {
		search.sortOrder.value = value as RecipeSortOrder;
		handleChangeUrl();
	};

	const handleChangePageSize = (value: string): void => {
		search.pageSize.value = Number(value);
		handleChangeUrl();
	};

	const handleChangePageIndex = (index: number) => {
		search.pageIndex.value = index;
		handleChangeUrl();
	};

	const getSuggestedRecipes = async (): Promise<ISelectItem[]> => {
		if (searchTerm.length >= 1) {
			await recipeList.search(searchTerm, props.archive ?? false);
		} else {
			recipeList.filtered = [];
		}
		setRecipeList(clone(recipeList));
		return recipeList.filtered;
	};

	const advancedSearchContainer = document.getElementById('advancedSearch');
	if (advancedSearchContainer) {
		advancedSearchContainer.addEventListener('shown.bs.collapse', function () {
			setRenderRanges(true);
		});
		advancedSearchContainer.addEventListener('hidden.bs.collapse', function () {
			setRenderRanges(false);
		});
	}

	const isDefaultView = (descriptor: string): boolean => {
		const defaultView: string | null = localStorage.getItem('recipe-search-view');
		if (descriptor === defaultView) {
			return true;
		}
		if (defaultView === null) {
			if (descriptor === 'list') {
				return true;
			}
		}
		return false;
	};

	const handleRestoreRecipe = async (recipe: Recipe): Promise<void> => {
		const result: SweetAlertResult = await Swal.fire({
			title: t('recipe:RESTORE_TITLE'),
			text: t('recipe:RESTORE_DIALOG'),
			icon: 'warning',
			showCancelButton: true,
			iconColor: '#00cc07',
			confirmButtonColor: '#00cc07',
			confirmButtonText: t('_general:RESTORE'),
			cancelButtonText: t('_general:CANCEL'),
		});

		if (result.value) {
			setIsLoading(true);
			try {
				await recipe.callApiRestore();
				await delay(3000);
				await Swal.fire({
					title: t('recipe:RESTORE_CONFIRMATION'),
					confirmButtonColor: '#00cc07',
				});
			} finally {
				await initialize();
				setIsLoading(false);
			}
		}
	};

	const handleArchiveRecipe = async (recipe: Recipe): Promise<void> => {
		const result: SweetAlertResult = await Swal.fire({
			title: t('recipe:ARCHIVE_TITLE'),
			text: t('recipe:ARCHIVE_DIALOG'),
			icon: 'warning',
			showCancelButton: true,
			iconColor: '#ff0004',
			confirmButtonColor: '#ff0004',
			confirmButtonText: t('_general:ARCHIVE'),
			cancelButtonText: t('_general:CANCEL'),
		});

		if (result.value) {
			setIsLoading(true);
			try {
				await recipe.callApiDelete();
				await delay(3000);
				await Swal.fire({
					title: t('recipe:ARCHIVE_CONFIRMATION'),
					confirmButtonColor: '#00cc07',
				});
			} finally {
				await initialize();
				setIsLoading(false);
			}
		}
	};

	const getSearchResultListData = (): IAirListRow[] => {
		return recipeList.all.map((recipe: Recipe) => {
			return {
				allergens: recipe.allergenList.all,
				archive: props.archive ?? false,
				author: recipe.lastEditor,
				buttons: renderButtons(recipe),
				calories: recipe.calories,
				carbonDioxideColor: recipe.carbonDioxideLabel?.color ?? '',
				carbonDioxideLabel: recipe.carbonDioxideLabel?.label ?? '',
				currency: reduxCurrency,
				dateLastEdited: recipe.lastEditedDateUtc,
				discriminator: AirListRowDiscriminatorName,
				hasNutriScore: recipe.hasNutriScore,
				href: `/recipe/detail/${recipe.id}`,
				id: recipe.id,
				imageUrl: getPicturePath(recipe.image, 348.5),
				nutriScore: recipe.nutriScore,
				prepareTime: recipe.prepareTime,
				price: recipe.price,
				seasonalityCode: getSeasonalityCode(recipe.seasonList.all),
				status: recipe.status?.name || '',
				statusColor: recipe.status?.color || '',
				tags: recipe.tagList.all,
				title: recipe.title,
				valueScope: reduxValueScope,
			};
		});
	};

	const handleAddToMenuOrMenuPlan = async (id: string): Promise<void> => {
		if (resourceType === 'menu' || resourceType === 'menuCopy') {
			await handleAddToMenu(id);
		} else if (params.menuPlanId && resourceType === 'menuPlan') {
			await handleAddToMenuPlan(id);
		}
	};
	const handler = useMenuHandlers();

	const handleAddToMenu = async (id: string) => {
		if (resourceType === 'menu' || resourceType === 'menuCopy') {
			const recipe = recipeList.all.filter((x) => x.id === id).first();

			if (params.menuId && resourceType === 'menu') {
				await handler.handleAddRecipe({
					currencyCode: reduxCurrency,
					recipeId: id,
					prepareTime: recipe.prepareTime,
					type: MenuEditType.EDIT,
					menuId: params.menuId,
				});
				navigate(`/menu/detail/${params.menuId}?edit=true`);
			} else if (params.menuId && resourceType === 'menuCopy') {
				await handler.handleAddRecipe({
					currencyCode: reduxCurrency,
					recipeId: id,
					prepareTime: recipe.prepareTime,
					type: MenuEditType.COPY,
					menuId: params.menuId,
				});
				navigate(`/menu/copy/${params.menuId}?edit=true`);
			} else {
				await handler.handleAddRecipe({
					currencyCode: reduxCurrency,
					recipeId: id,
					prepareTime: recipe.prepareTime,
					type: MenuEditType.NEW,
					menuId: null,
				});
				navigate('/menu/new?edit=true');
			}
		}
	};

	const handleAddToMenuPlan = async (id: string): Promise<void> => {
		const menuPlanId: string | undefined = params.menuPlanId;
		const rowIndex: string | undefined = params.menuPlanRowIndex;
		const day: string | undefined = params.menuPlanDay;
		if (menuPlanId && rowIndex && day) {
			const menuPlan = new MenuPlan();
			await menuPlan.callApiGet(menuPlanId, reduxCurrency);
			menuPlan.setRecipeToRow(Number(day), Number(rowIndex), id);
			await menuPlan.saveToApi(reduxCurrency);
		}
		navigate(`/menuPlan/detail/${params.menuPlanId}`);
	};

	const createClickFunction = (id: string): (() => void) => {
		return () => {
			navigate(`/recipe/detail/${id}`);
		};
	};

	const handleCopyRecipe = (id: string): (() => void) => {
		return () => {
			navigate(`/recipe/copy/${id}`);
		};
	};

	const handleImageSearch = async (input: FormData | null) => {
		if (input) {
			setIsLoading(true);
			const response = await apiRecipeImageSearch.callApi(input, numberOfRecommendations);
			if (response.hasValue()) {
				const result = response.get();
				if (result.isEmpty()) {
					search.recipeIds = null;
				} else {
					search.recipeIds = result;
				}

				search.sortOrder.value = RecipeSortOrder.RecipeFilterOrder;
				handleChangeUrl();
			}
		}
	};

	const getButtonDataRestore = (recipe: Recipe): BtnData | undefined => {
		if (props.archive) {
			return {
				onClick: () => handleRestoreRecipe(recipe),
			};
		}
	};

	const getButtonDataCopy = (id: string): BtnData | undefined => {
		if (
			!props.archive &&
			!params.menuId &&
			!isNewMenu &&
			!params.menuPlanId &&
			isRecipeEditor
		) {
			return {
				onClick: handleCopyRecipe(id),
			};
		}
	};

	const getButtonDataEdit = (id: string): BtnData | undefined => {
		if (
			!props.archive &&
			!params.menuId &&
			!isNewMenu &&
			!params.menuPlanId &&
			isRecipeEditor
		) {
			return {
				onClick: createClickFunction(id),
			};
		}
	};

	const getButtonDataView = (id: string): BtnData | undefined => {
		if (
			!props.archive &&
			!params.menuId &&
			!isNewMenu &&
			!params.menuPlanId &&
			!isRecipeEditor
		) {
			return {
				onClick: createClickFunction(id),
			};
		}
	};

	const getButtonDataCart = (id: string): BtnData | undefined => {
		if (props.archive) return undefined;
		if (resourceType === 'menu') return undefined;
		if (resourceType === 'menuPlan') return undefined;
		return {
			onClick: () => {
				carts.openModal(id, EntityType.RECIPE);
			},
		};
	};

	const getButtonDataArchive = (recipe: Recipe): BtnData | undefined => {
		if (
			!props.archive &&
			!params.menuId &&
			!isNewMenu &&
			!params.menuPlanId &&
			isRecipeEditor
		) {
			return {
				onClick: () => handleArchiveRecipe(recipe),
			};
		}
	};

	const getButtonDataAdd = (id: string): BtnData | undefined => {
		if (!props.archive) {
			if (
				resourceType == 'menu' ||
				resourceType == 'menuCopy' ||
				resourceType == 'menuPlan'
			) {
				return {
					onClick: () => handleAddToMenuOrMenuPlan(id),
				};
			}
		}
	};

	const renderButtons = (recipe: Recipe): JSX.Element => {
		return (
			<BtnGroup
				add={getButtonDataAdd(recipe.id)}
				copy={getButtonDataCopy(recipe.id)}
				archive={getButtonDataArchive(recipe)}
				edit={getButtonDataEdit(recipe.id)}
				restore={getButtonDataRestore(recipe)}
				view={getButtonDataView(recipe.id)}
				cart={getButtonDataCart(recipe.id)}
			/>
		);
	};

	const getSearchResultThumbnailData = (): ThumbnailCard[] => {
		return recipeList.all.map((recipe: Recipe) => {
			return {
				archive: props.archive ?? false,
				author: recipe.lastEditor,
				buttons: renderButtons(recipe),
				calories: recipe.calories,
				currency: reduxCurrency,
				dateLastEdited: recipe.lastEditedDateUtc,
				href: `/recipe/detail/${recipe.id}`,
				id: recipe.id,
				imageUrl: getPicturePath(recipe.image, 348.5),
				prepareTime: recipe.prepareTime,
				price: recipe.price,
				status: recipe.status?.name || '',
				statusColor: recipe.status?.color || '',
				title: recipe.title,
				valueScope: reduxValueScope,
			};
		});
	};

	const renderTitle = (): JSX.Element => {
		if (props.archive) {
			return <h1>{t('_general:RECIPE_ARCHIVE')}</h1>;
		} else {
			if (resourceType == 'menu' || resourceType == 'menuPlan') {
				return <h1>{t('menu:ADD_RECIPE')}</h1>;
			} else {
				return <h1>{t('_general:SEARCH_RECIPE')}</h1>;
			}
		}
	};

	return (
		<main className="container">
			<div className="d-flex justify-content-between">
				{renderTitle()}
				<div className="d-flex" style={{ gap: '1rem', alignItems: 'center' }}>
				<button
					className='btn btn-sm btn-outline-danger'
					style={{ maxHeight: '4rem' }}
					data-bs-toggle="tooltip"
					data-bs-placement="top"
					title="Archive"
				>
					<FontAwesomeIcon icon={faArchive} />
				</button>
				<button
					className='btn btn-sm btn-outline-primary'
					style={{ maxHeight: '4rem' }}
					data-bs-toggle="tooltip"
					data-bs-placement="top"
					title="Category"
				>
					<FontAwesomeIcon icon={faFolders} />
				</button>
					<RenderIf
						condition={
							!props.archive && !params.menuId && !params.menuPlanId && isRecipeEditor
						}
					>
						<BtnNew link={'/recipe/new'} name={t('recipe:CREATE_RECIPE')} />
					</RenderIf>
				</div>
			</div>
			<hr style={{ color: '#DDDFFF', height: '2px' }} />
			<div className="row">
				<div className="col-lg-6">
					<div className="input-group mb-3">
						<AdvancedSearchInput
							debounceMilliSeconds={350}
							getSuggestedItems={getSuggestedRecipes}
							handleImageSearch={handleImageSearch}
							handleSearch={handleSearch}
							searchTerm={searchTerm}
							setSearchTerm={setSearchTerm}
							triggerSearchLetterAmount={1}
						/>
					</div>
				</div>
				<div className="col-lg-3">
					<button
						id="advancedSearchButton"
						className="btn btn-primary"
						type="button"
						onClick={() => setRenderRanges(true)}
						data-bs-toggle="collapse"
						data-bs-target="#advancedSearch"
						aria-expanded="true"
						aria-controls="advancedSearch"
						disabled={searchIsLoading}
					>
						{searchIsLoading ? <Spinner animation="border" size="sm" /> : ''}
						{t('_general:ADVANCED_FILTER')}
					</button>
				</div>
				<div className="col-lg-3 text-end">
					<div className="d-flex flex-row justify-content-end">
						<DropdownPageSize
							className="me-2"
							listFunction={handleChangePageSize}
							currentValue={String(search.getPageSize())}
						/>
						<DropdownSortOrder
							type={'recipe'}
							className="me-3"
							listFunction={handleChangeSortOrder}
							currentValue={search.getSortOrder()}
							showRelevance={Boolean(search.recipeIds && search.recipeIds.length)}
						/>
						<NavigationPillsButtons
							className="justify-content-end"
							button1={{
								icon: <i className="bi bi-border-all"></i>,
								descriptor: 'list',
								default: isDefaultView('list'),
							}}
							button2={{
								icon: <i className="bi bi-list-task"></i>,
								descriptor: 'thumbnail',
								default: isDefaultView('thumbnail'),
							}}
							localStorageItemName="recipe-search-view"
						/>
					</div>
				</div>
			</div>
			<div className={'collapse'} id="advancedSearch">
				<div className="card card-body mb-3">
					<RecipeAdvancedSearch
						disabled={recipeList.totalCount === 0}
						search={search}
						setSearch={setSearch}
						handleChangeUrl={hadleChangeAdvancedSearchUrl}
						handleReset={handleReset}
						renderRanges={renderRanges}
					/>
				</div>
			</div>
			<LoadingAnimation isLoading={isLoading} />
			<p>{`${recipeList.totalCount} ${t('_general:RECIPES')}`}</p>

			<NavigationPillsContent
				isLoading={isLoading}
				content1={{
					content: <SearchResultList data={getSearchResultListData()} />,
					descriptor: 'thumbnail',
					default: isDefaultView('thumbnail'),
				}}
				content2={{
					content: <SearchResultThumbnail data={getSearchResultThumbnailData()} />,
					descriptor: 'list',
					default: isDefaultView('list'),
				}}
			/>
			<Pagination
				countOfAllListItems={recipeList.totalCount}
				pageIndex={search.getPageIndex()}
				setPageIndex={handleChangePageIndex}
				listItemAmount={search.getPageSize()}
				maxNumberOfButtons={maxNumberOfButtons}
			/>
		</main>
	);
};

export default RecipeSearchComponent;
